import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Card,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

// Import section components
import ScopeSection from '../components/questionnaire/ScopeSection';
import AudienceSection from '../components/questionnaire/AudienceSection';
import UserVolumeSection from '../components/questionnaire/UserVolumeSection';
import DeviceSupportSection from '../components/questionnaire/DeviceSupportSection';
import TechnologySection from '../components/questionnaire/TechnologySection';
import DocumentUploadSection from '../components/questionnaire/DocumentUploadSection';
import CloudArchitectureSection from '../components/questionnaire/CloudArchitectureSection';
import RepositorySection from '../components/questionnaire/RepositorySection';
import DatabaseSection from '../components/questionnaire/DatabaseSection';
import IntegrationSection from '../components/questionnaire/IntegrationSection';
import ReviewSection from '../components/questionnaire/ReviewSection';

import useProject from '../hooks/useProject';
import useQuestionnaire from '../hooks/useQuestionnaire';
import useNotification from '../hooks/useNotification';

const Questionnaire = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { project: projectData, refetchProject } = useProject({ projectId });
  const { getResponses, saveResponses, submitQuestionnaire, generateAPD } = useQuestionnaire();
  const { showNotification } = useNotification();
  
  const [activeStep, setActiveStep] = useState(0);
  const [project, setProject] = useState(null);
  const [responses, setResponses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);
  
  // Define all the sections
  const sections = [
    { id: 'scope', label: 'Project Scope', component: ScopeSection },
    { id: 'audience', label: 'Target Audience', component: AudienceSection },
    { id: 'userVolume', label: 'User Volume', component: UserVolumeSection },
    { id: 'deviceSupport', label: 'Device Support', component: DeviceSupportSection },
    { id: 'technology', label: 'Technology Stack', component: TechnologySection },
    { id: 'documents', label: 'Documentation', component: DocumentUploadSection },
    { id: 'cloudArchitecture', label: 'Cloud Architecture', component: CloudArchitectureSection },
    { id: 'repository', label: 'Repository', component: RepositorySection },
    { id: 'database', label: 'Database Schema', component: DatabaseSection },
    { id: 'integrations', label: 'Third-Party Integrations', component: IntegrationSection },
    { id: 'review', label: 'Review & Submit', component: ReviewSection },
  ];
  
  // Load project and responses - with a flag to prevent loops
  useEffect(() => {
    let isMounted = true;
    let dataLoaded = false;
    
    const loadData = async () => {
      // Only load once per mount
      if (dataLoaded) return;
      dataLoaded = true;
      
      try {
        if (isMounted) {
          setIsLoading(true);
          setError('');
        }
        
        // Load project details
        await refetchProject();
        
        if (!isMounted) return;
        
        setProject(projectData);
        
        // Load existing responses (if any)
        const responseData = await getResponses(projectId);
        
        if (!isMounted) return;
        
        setResponses(responseData || {});
        
        // Calculate progress
        calculateProgress(responseData);
      } catch (err) {
        if (isMounted) {
          console.error('Error loading data:', err);
          setError('Failed to load project data. Please try again.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [projectId, refetchProject, projectData, getResponses]);
  
  // Calculate completion progress
  const calculateProgress = (data) => {
    if (!data) {
      setProgress(0);
      return;
    }
    
    const totalSections = sections.length - 1; // Exclude review section
    let completedSections = 0;
    
    sections.forEach((section, index) => {
      if (index < totalSections && data[section.id] && Object.keys(data[section.id]).length > 0) {
        completedSections++;
      }
    });
    
    setProgress(Math.round((completedSections / totalSections) * 100));
  };
  
  // Handle section data changes
  const handleSectionDataChange = (sectionId, sectionData) => {
    // Using a function reference instead of directly setting state
    // to avoid unnecessary renders
    const newSectionData = JSON.stringify(sectionData);
    const prevSectionData = JSON.stringify(responses[sectionId] || {});
    
    // Only update if data has actually changed
    if (newSectionData !== prevSectionData) {
      setResponses(prevResponses => {
        const updatedResponses = {
          ...prevResponses,
          [sectionId]: sectionData
        };
        
        calculateProgress(updatedResponses);
        return updatedResponses;
      });
    }
  };
  
  // Handle saving progress
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError('');
      
      await saveResponses(projectId, responses);
      
      showNotification({
        message: 'Progress saved successfully',
        type: 'success'
      });
    } catch (err) {
      console.error('Error saving responses:', err);
      setError('Failed to save progress. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle next step
  const handleNext = async () => {
    // If we're at the last section, handle submission
    if (activeStep === sections.length - 1) {
      await handleSubmit();
      return;
    }
    
    // Otherwise, save current progress and move to next step
    try {
      setIsSaving(true);
      await saveResponses(projectId, responses);
      setActiveStep(prevStep => prevStep + 1);
    } catch (err) {
      console.error('Error saving before next step:', err);
      setError('Failed to save progress. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle previous step
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  // Handle final submission
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError('');
      
      // First save all responses
      await saveResponses(projectId, responses);
      
      // Then submit the questionnaire as completed
      await submitQuestionnaire(projectId);
      
      // Generate the APD document
      const apdDocument = await generateAPD(projectId);
      
      // Log the document structure to see what fields are available
      console.log('Generated APD document:', apdDocument);
      
      showNotification({
        message: 'PRD data submitted successfully! Generating APD document...',
        type: 'success'
      });
      
      // Navigate to the generated document using _id field
      if (apdDocument && apdDocument._id) {
        navigate(`/projects/${projectId}/documents/${apdDocument._id}`);
      } else {
        console.error('Document ID is missing or undefined', apdDocument);
        setError('Document was generated but the ID is missing. Please go to the Documents section to view it.');
        
        // Still allow the user to navigate to the documents page
        setTimeout(() => {
          navigate(`/projects/${projectId}/documents`);
        }, 3000);
      }
    } catch (err) {
      console.error('Error submitting questionnaire:', err);
      setError('Failed to submit the questionnaire. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle exit
  const handleExitClick = () => {
    // If there are unsaved changes, show confirmation dialog
    setExitDialogOpen(true);
  };
  
  const handleConfirmExit = () => {
    setExitDialogOpen(false);
    navigate(`/projects/${projectId}`);
  };
  
  const handleCancelExit = () => {
    setExitDialogOpen(false);
  };
  
  // Render current section component
  const renderSection = () => {
    const CurrentSection = sections[activeStep].component;
    return (
      <CurrentSection
        projectId={projectId}
        data={responses[sections[activeStep].id] || {}}
        onChange={(sectionData) => handleSectionDataChange(sections[activeStep].id, sectionData)}
      />
    );
  };
  
  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading project data...
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mt: 4, mb: 8 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {project?.name}: PRD Questionnaire
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" paragraph>
            Complete the following sections to generate your Product Requirements Document.
          </Typography>
          
          <Card variant="outlined" sx={{ mt: 2, mb: 4, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                Overall Progress:
              </Typography>
              <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ ml: 1 }}>
                {progress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving}
                sx={{ mr: 1 }}
              >
                {isSaving ? 'Saving...' : 'Save Progress'}
              </Button>
              
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<ExitToAppIcon />}
                onClick={handleExitClick}
                disabled={isSaving || isSubmitting}
              >
                Exit
              </Button>
            </Box>
          </Card>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
        </Box>
        
        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel>
          {sections.map((section, index) => (
            <Step key={section.id} completed={responses[section.id] && Object.keys(responses[section.id]).length > 0}>
              <StepLabel>{section.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {/* Section Content */}
        <Paper elevation={3} sx={{ mt: 4, p: 4, borderRadius: 2 }}>
          <Box sx={{ minHeight: '50vh' }}>
            {renderSection()}
          </Box>
          
          <Divider sx={{ my: 3 }} />
          
          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={activeStep === 0 || isSaving || isSubmitting}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isSaving || isSubmitting}
              endIcon={activeStep === sections.length - 1 ? <CheckCircleIcon /> : null}
            >
              {isSaving ? (
                <CircularProgress size={24} />
              ) : activeStep === sections.length - 1 ? (
                isSubmitting ? 'Submitting...' : 'Submit and Generate APD'
              ) : (
                'Save & Continue'
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
      
      {/* Exit Confirmation Dialog */}
      <Dialog
        open={exitDialogOpen}
        onClose={handleCancelExit}
      >
        <DialogTitle>Exit Questionnaire?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to exit? Your current progress has been saved, but you'll need to complete the questionnaire to generate your PRD.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelExit} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmExit} color="error">
            Exit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Questionnaire;